import { useEffect } from "react"
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from "@hookform/resolvers/yup"
import { TextField } from "../../../../components/styledFormElements/TextField"
import { twElementStyles } from "../../../../twElementStyles"
import { ToggleField } from "../../../../components/styledFormElements/ToggleField"
import { useAddRole, useUpdateRole } from "../../../../hooks/role"
import { Loader } from "../../../../components/loader"
import { Button } from "../../../../components/ui/button"
import { Actions } from "../../../../constants/other"
import { getErrorResponse, toTitleCase } from "../../../../services/util"
import { useNotificationBar } from "../../../../context/notificationContext"
import _ from "lodash"
interface ComponentProps {
    input_data: any
    setIsOpen: React.Dispatch<React.SetStateAction<any>>,
    onSubmitHandler?: Function
    permissionSubjects?: any[]
}

const schema = yup.object({
    role_name: yup.string().required("First name is required !")
}).required()
export const AddUpdateRole: React.FC<ComponentProps> = ({ input_data, setIsOpen, onSubmitHandler, permissionSubjects = [] }) => {

    const { register, formState: { errors, dirtyFields }, watch, reset, handleSubmit } = useForm({
        resolver: yupResolver(schema),
    })
    const notification = useNotificationBar()
    const { mutate: addRole, isLoading: isLoadingAdd } = useAddRole()
    const { mutate: updateRole, isLoading: isLoadingUpdate } = useUpdateRole()
    let isLoading = isLoadingAdd || isLoadingUpdate

    const setPermissionValues = (permissions: any[], permissionSubjects: any[]) => {
        let permissionObj: any = {}
        permissionSubjects?.forEach(p => permissionObj[p] = [])
        permissions?.forEach(p => permissionObj[p.subject] = p.action)
        return permissionObj;
    }

    useEffect(() => {
        if (input_data && permissionSubjects?.length)
            reset({ ...input_data, is_active: !!input_data?.is_active, permissions: setPermissionValues(input_data?.permissions ?? [], permissionSubjects) })
    }, [input_data, reset, permissionSubjects])

    const onSubmit = (values: any) => {
        let permissions = Object.keys(values?.permissions)?.filter(p => values?.permissions?.[p]?.length)?.map(p => ({
            subject: p,
            action: (values?.permissions?.[p]?.includes('manage') ? ['manage'] : values?.permissions?.[p]) ?? []
        }))
        const all = permissions.find(p => p.subject === 'all')
        const update = {
            ...values,
            is_active: values?.is_active ? 1 : 0,
            permissions: all && permissions?.length === 1 ? [all] : permissions
        }

        if (update?.id) {
            const partial: any = {}
            Object.entries(update).forEach(([key, value]) => dirtyFields[key] && (partial[key] = value))
            if (_.isEmpty(partial)) {
                return onSubmitHandler && onSubmitHandler(update);
            }
            updateRole({
                data: partial,
                role_id: update?.id
            }, {
                onError(error: any) {
                    notification.push(getErrorResponse(error))
                },
                onSuccess(data, variables, context) {
                    notification.push({
                        title: 'Role Updated',
                        message: `Role "${update.role_name}" has been updated successfully !`,
                        type: 'success'
                    })
                    onSubmitHandler && onSubmitHandler(update);
                },
            })
        }
        else {

            addRole({
                data: update
            }, {
                onError(error: any) {
                    notification.push(getErrorResponse(error))
                },
                onSuccess(data, variables, context) {
                    notification.push({
                        title: 'Role Created',
                        message: `Role "${update.role_name}" has been created successfully !`,
                        type: 'success'
                    })
                    onSubmitHandler && onSubmitHandler(update);
                },
            })
        }
    }

    const shouldChecked = (permission, action) => {
        return (watch(`permissions.${permission}`)?.includes('manage') && action !== 'manage') || ((watch(`permissions.all`)?.includes(action) || watch(`permissions.all`)?.includes('manage')) && permission !== 'all')
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-2 gap-4">
            <TextField register={register('role_name')} errors={errors} label="Role name: " placeholder="Role name:" />
            <ToggleField register={register('is_active')} value={1} label="Status: " display={watch('is_active') ? 'Active' : 'Inactive'} />
            <div className="col-span-2">
                <label className={'text-sm block font-semibold mb-3'}>
                    Permissions:
                </label>
                <div className="min-h-[100px] w-full">
                    <div className="grid grid-cols-1 gap-2 max-h-[50vh] overflow-auto pr-1">
                        {
                            permissionSubjects?.map((permission, index) => (

                                <div className="w-full">
                                    <ul className="items-center w-full min-w-max text-sm font-medium text-gray-900 bg-white rounded-lg border border-gray-200 sm:flex dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                                        <li className={`w-full sm:w-44 sm:shrink-0 rounded-l-lg border-b border-gray-200 sm:border-b-0 sm:border-r dark:border-gray-600 bg-slate-100 dark:bg-gray-700`}>
                                            <div className="flex items-center pl-3">
                                                <label htmlFor={permission + 'manage'} className="py-2 ml-2 w-full text-sm text-gray-900 dark:text-gray-300">{toTitleCase(permission)}</label>
                                            </div>
                                        </li>
                                        {
                                            [...Object.values(Actions), ...(permission === 'Article' ? ['review', 'publish'] : [])].map(action => (
                                                <li key={action} className={`w-full sm:w-auto sm:shrink-0 border-b border-gray-200 sm:border-b-0 sm:border-r dark:border-gray-600 ${permission === 'all' ? 'bg-slate-200  dark:bg-gray-700' : 'bg-white dark:bg-gray-800'} `}>
                                                    <div className="flex items-center pl-3 pr-3 whitespace-nowrap">
                                                        <input checked={shouldChecked(permission, action) || watch(`permissions.${permission}`)?.includes(action)} disabled={shouldChecked(permission, action)} {...register(`permissions.${permission}`)} value={action} id={permission + action} type="checkbox" className="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-700 dark:bg-gray-600 dark:border-gray-500 focus:ring-0 disabled:text-gray-400 dark:disabled:bg-gray-600 peer dark:checked:bg-blue-700" />
                                                        <label htmlFor={permission + action} className="py-2 ml-2 text-sm text-black dark:text-white peer-disabled:text-gray-400  dark:peer-disabled:text-gray-500">{toTitleCase(action)}</label>
                                                    </div>
                                                </li>
                                            ))
                                        }
                                    </ul>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
            <div className="col-span-full flex">
                <div className="ml-auto flex gap-2 items-center">
                    <Button color="basic" disabled={isLoading} type="button" onClick={() => setIsOpen(false)} className={twElementStyles.twButtonBasic} >Cancel</Button>
                    <Button color="blue-accent" disabled={isLoading} type="submit" className={twElementStyles.twButtonElegant} >
                        {isLoading ? (
                            <><Loader /> Saving ...</>
                        ) : 'Save'}
                    </Button>
                </div>
            </div>
        </form>
    )
}